import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Hub } from "../lib/useHub";
import { useTheme } from "../lib/useTheme";
import AgentBox from "./AgentBox";
import CliInstallButton from "./CliInstallButton";
import NotificationBell from "./NotificationBell";
import VersionBadge from "./VersionBadge";
import Wordmark from "./Wordmark";

const initial = (s: string) => (s.trim().charAt(0) || "?").toUpperCase();

// Sun / moon glyphs for the theme toggle, drawn in the same stroke style as the
// other topbar icons.
function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

// A small robot glyph for agent rows.
function RobotIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="8" width="16" height="12" rx="2" />
      <path d="M12 8V4M9 4h6" />
      <circle cx="9" cy="14" r="1" />
      <circle cx="15" cy="14" r="1" />
    </svg>
  );
}

// A small person glyph for human rows.
function PersonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a8 8 0 0 1 16 0v1" />
    </svg>
  );
}

// Top chrome bar: Incode wordmark + tool name (left) and the live presence pill
// (right) — agents and humans connected to the Omni Hub. The popup
// has Agents / Humans tabs with a filter, rich rows per entity, and a green dot
// for whoever is live on the agent-hub.
export default function TopBar({ hub }: { hub: Hub }) {
  const { theme, toggle: toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"agents" | "humans">("agents");
  const [query, setQuery] = useState("");
  // The dropdown renders in a portal on <body> so it can't be covered by page
  // content (it doesn't depend on the topbar's stacking context). Anchor it to
  // the pill's on-screen position, captured when opening.
  const pillRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; right: number }>({ top: 64, right: 24 });

  function toggleOpen() {
    if (!open && pillRef.current) {
      const r = pillRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
    }
    setOpen((o) => !o);
  }

  // Agents + humans currently live on the hub. Both are presence views, so only
  // online entities are listed (offline/stale registrations are excluded).
  const agents = useMemo(
    () =>
      Object.values(hub.agents)
        .filter((a) => a.status === "online")
        .sort((a, b) => a.name.localeCompare(b.name)),
    [hub.agents],
  );
  const humans = useMemo(
    () =>
      Object.values(hub.humans)
        .filter((h) => h.status === "online")
        .sort((a, b) => a.name.localeCompare(b.name)),
    [hub.humans],
  );
  const onlineAgents = agents.length;
  const onlineCount = onlineAgents + humans.length;

  const q = query.trim().toLowerCase();
  const shownAgents = q
    ? agents.filter((a) => `${a.name} ${a.account ?? ""} ${a.org ?? ""}`.toLowerCase().includes(q))
    : agents;
  const shownHumans = q
    ? humans.filter((h) => `${h.name} ${h.email} ${h.position ?? ""}`.toLowerCase().includes(q))
    : humans;

  return (
    <header className="topbar">
      <div className="topbar-brand">
        <a className="wordmark" href="#" aria-label="Incode home">
          <Wordmark />
        </a>
        <span className="topbar-divider" aria-hidden="true"></span>
        <span className="tool-name">Omni Hub</span>
        <VersionBadge />
      </div>

      <div className="topbar-right">
      {/* CLI install launcher — a pill next to the presence pill. */}
      <CliInstallButton />

      {/* Presence pill — agents + humans connected to the Omni Hub. */}
      <div className="presence">
        <button
          ref={pillRef}
          className="presence-pill"
          onClick={toggleOpen}
          title={`${onlineAgents} agent${onlineAgents === 1 ? "" : "s"} and ${humans.length} human${humans.length === 1 ? "" : "s"} online`}
          aria-expanded={open}
        >
          <span className={`presence-dot${onlineCount > 0 ? " online" : ""}`} />
          <span className="presence-seg" title="Agents online"><RobotIcon /><span className="presence-count">{onlineAgents}</span></span>
          <span className="presence-seg" title="Humans online"><PersonIcon /><span className="presence-count">{humans.length}</span></span>
        </button>

        {open && createPortal(
          <>
            <div className="presence-backdrop" onClick={() => setOpen(false)} />
            <div className="presence-pop" role="menu" style={{ top: coords.top, right: coords.right }}>
              <div className="presence-tabs">
                <button
                  className={`presence-tab${tab === "agents" ? " active" : ""}`}
                  onClick={() => setTab("agents")}
                >
                  Agents <span className="presence-tab-count">{agents.length}</span>
                </button>
                <button
                  className={`presence-tab${tab === "humans" ? " active" : ""}`}
                  onClick={() => setTab("humans")}
                >
                  Humans <span className="presence-tab-count">{humans.length}</span>
                </button>
              </div>

              <div className="presence-filter">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Filter ${tab}…`}
                  autoFocus
                />
              </div>

              <div className="presence-list">
                {tab === "agents" ? (
                  shownAgents.length === 0 ? (
                    <div className="presence-empty">{query ? "No matching agents" : "No agents online"}</div>
                  ) : (
                    shownAgents.map((a) => <AgentBox key={a.id} agent={a} variant="expanded" />)
                  )
                ) : shownHumans.length === 0 ? (
                  <div className="presence-empty">{query ? "No matching people" : "No one connected"}</div>
                ) : (
                  shownHumans.map((h) => (
                    <div className="presence-card" key={h.id}>
                      {h.image ? (
                        <img className="presence-ava img" src={h.image} alt="" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="presence-ava human">{initial(h.name || h.email)}</div>
                      )}
                      <div className="presence-card-main">
                        <div className="presence-card-name">
                          {h.name}
                          <span className="presence-dot online" />
                        </div>
                        <div className="presence-card-sub">{h.email}</div>
                        {h.position && <div className="presence-card-tag">{h.position}</div>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
      </div>

      {/* Notifications — the global bell, between the presence (active) box and
          the theme toggle. Per-user; spans DevHub review + session + task events. */}
      <NotificationBell />

      {/* Light/dark theme toggle — far right, after the presence (active) box. */}
      <button
        type="button"
        className="theme-toggle"
        onClick={toggleTheme}
        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      >
        {theme === "dark" ? <SunIcon /> : <MoonIcon />}
      </button>
      </div>
    </header>
  );
}
